import React, { useEffect, useRef, useState } from "react";
import { useOrderChangesDialog } from "../../Components/orders/useOrderChangesDialog";
import { useQuery } from "@tanstack/react-query";
import {
  checkDuplicatePhoneCount,
  fetchTeePlusOrders,
  fetchTeePlusOrdersSummary,
  fetchTeePlusStock,
  refreshTeePlusStock,
  updateOrderStatus,
  updateOrderStatusBulk,
  deleteOrder,
} from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle,
  teePlusPackedItemData,
  caseStyleNameFromDetail,
  orderMatchesCaseStyleFilter,
  CASE_STYLE_FILTER_ALL,
} from "../../utils/orderLineDisplay";
import CaseStyleOrderFilter from "../../Components/orders/CaseStyleOrderFilter";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import { copyTextToClipboard } from "../../utils/productionSource";
import {
  AlertTriangle,
  Archive,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Download,
  DollarSign,
  Edit,
  Hash,
  Link as LinkIcon,
  Loader,
  Map as MapIcon,
  MapPin,
  Package,
  Phone,
  Printer,
  RefreshCw,
  Save,
  Search,
  Send,
  Shirt,
  Trash2,
  User,
} from "lucide-react";

const CONFIRMED_FROM_SECOND_PHONE_NOTE = "تم التأكيد مو الرقم التاني";
const CONFIRMED_FROM_SECOND_PHONE_LABEL = "تم التأكيد من الرقم التاني";

const isConfirmedFromSecondPhone = (note) =>
  note && note.includes(CONFIRMED_FROM_SECOND_PHONE_NOTE);

const buildFirstItemLine = (customerBase, itemLine, note) =>
  isConfirmedFromSecondPhone(note)
    ? `${customerBase}${CONFIRMED_FROM_SECOND_PHONE_LABEL}\t\t${itemLine}`
    : `${customerBase}\t\t${itemLine}`;

const STOCK_TABS = [
  { id: "InStock", label: "متوفر", english: "In Stock", countKey: "inStock", active: "bg-emerald-600 text-white", idle: "bg-white text-emerald-800 border border-emerald-200" },
  { id: "Limited", label: "محدود", english: "Limited", countKey: "limited", active: "bg-amber-500 text-white", idle: "bg-white text-amber-800 border border-amber-200" },
  { id: "OutOfStock", label: "غير متوفر", english: "Out of Stock", countKey: "outOfStock", active: "bg-rose-600 text-white", idle: "bg-white text-rose-800 border border-rose-200" },
  { id: "NotExist", label: "غير موجود", english: "Not Exist", countKey: "notExist", active: "bg-slate-700 text-white", idle: "bg-white text-slate-800 border border-slate-200" },
];

const formatStockCheckTime = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const TeePlusOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const { changesDialog, openOrderChanges } = useOrderChangesDialog(async (savedOrderId) => {
    setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== savedOrderId));
    toast.success("تم تحديث الطلب إلى Changes بنجاح");
  });
  
  const adminUser = isAdmin(authState.role);

  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");
  const [searchTerm, setSearchTerm] = useState("");
  const [designFilter, setDesignFilter] = useState("all");
  const [caseStyleFilter, setCaseStyleFilter] = useState(CASE_STYLE_FILTER_ALL);
  const [pageSize, setPageSize] = useState(50);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [stockTab, setStockTab] = useState("InStock");
  const [stockInfo, setStockInfo] = useState(null);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [isRefreshingStock, setIsRefreshingStock] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(() => new Map());
  const ordersRequestRef = useRef(0);
  const stockTabRef = useRef(stockTab);
  const stockLoadedRef = useRef(false);
  stockTabRef.current = stockTab;

  const { data: teePlusSummary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ["teePlusOrdersSummary"],
    queryFn: fetchTeePlusOrdersSummary,
    enabled: adminUser,
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0, totalProfit: 0 },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const enrichOrders = async (data) =>
    Promise.all(
      data.map(async (order) => {
        const enrichedItems = [];
        for (const detail of order.orderDetails || []) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;
          const baseItem = {
            id: detail.designId,
            name: design.Name,
            phoneType: type.name || "",
            caseStyleName: caseStyleNameFromDetail(detail),
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code?.startsWith("CUSTOM")
              ? `${API_BASE_URL_V2}${design.driveLink}`
              : design.driveLink || "",
            code: design.code || "None",
          };
          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({ ...baseItem, quantityIndex: i + 1 });
          }
        }
        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          teePlusStockStatus: order.teePlusStockStatus || null,
        };
      })
    );

  const loadStock = async () => {
    setIsLoadingStock(true);
    try {
      const data = await fetchTeePlusStock();
      setStockInfo(data);
      if (!data?.success) setError("Stock Check Failed");
      else setError(null);
    } catch (err) {
      console.error(err);
      setStockInfo({ success: false, error: "Stock Check Failed", orders: {} });
      setError("Stock Check Failed");
    } finally {
      setIsLoadingStock(false);
    }
  };

  const loadInitialOrders = async (tab = stockTab) => {
    const requestId = ++ordersRequestRef.current;
    setIsOrdersLoading(true);
    setLoadedOrders([]);
    try {
      const data = await fetchTeePlusOrders(0, pageSize, tab);
      const enriched = await enrichOrders(data);
      if (requestId !== ordersRequestRef.current) return;
      setHasMore(!(data.length === 0 || data.length < pageSize));
      setLoadedOrders(enriched);
      setCurrentPage(1);
    } catch (err) {
      if (requestId !== ordersRequestRef.current) return;
      console.error(err);
      setError("فشل في تحميل طلبات Tee Plus.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      if (requestId === ordersRequestRef.current) setIsOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    let cancelled = false;
    (async () => {
      await loadStock();
      if (cancelled) return;
      stockLoadedRef.current = true;
      await loadInitialOrders(stockTabRef.current);
    })();
    return () => {
      cancelled = true;
    };
  }, [authState.role, navigate]);

  useEffect(() => {
    if (!stockLoadedRef.current) return;
    if (!canViewOrders(authState.role)) return;
    loadInitialOrders(stockTab);
  }, [stockTab]);

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchTeePlusOrders(currentPage, pageSize, stockTab);
      if (data.length === 0 || data.length < pageSize) setHasMore(false);
      const enriched = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enriched]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setIsOrdersLoading(true);
    setLoadedOrders([]);
    try {
      const data = await fetchTeePlusOrders(0, newSize, stockTab);
      setLoadedOrders(await enrichOrders(data));
      setCurrentPage(1);
      setHasMore(data.length === newSize);
    } catch (err) {
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const handleRefreshStock = async () => {
    setIsRefreshingStock(true);
    try {
      const data = await refreshTeePlusStock();
      setStockInfo(data);
      if (!data?.success) {
        setError("Stock Check Failed");
        toast.error("Stock Check Failed");
      } else {
        setError(null);
        toast.success("تم تحديث مخزون TeePlus");
      }
      const orders = await fetchTeePlusOrders(0, pageSize, stockTab);
      setHasMore(!(orders.length === 0 || orders.length < pageSize));
      setLoadedOrders(await enrichOrders(orders));
      setCurrentPage(1);
      setSelectedOrders(new Map());
    } catch (err) {
      console.error(err);
      setError("Stock Check Failed");
      toast.error("Stock Check Failed");
    } finally {
      setIsRefreshingStock(false);
    }
  };

  const toggleSelectOrder = (order) => {
    setSelectedOrders((prev) => {
      const next = new Map(prev);
      if (next.has(order.orderNumber)) next.delete(order.orderNumber);
      else next.set(order.orderNumber, order);
      return next;
    });
  };

  const setSelectAllVisible = (checked, orders) => {
    setSelectedOrders((prev) => {
      const next = new Map(prev);
      orders.forEach((order) => {
        if (checked) next.set(order.orderNumber, order);
        else next.delete(order.orderNumber);
      });
      return next;
    });
  };

  const forgetOrders = (orderNumbers) => {
    setSelectedOrders((prev) => {
      const next = new Map(prev);
      orderNumbers.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const isOrderCustom = (order) =>
    order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));

  const isOrderNonCustom = (order) => !isOrderCustom(order);

  const matchesListFilters = (order) => {
    if (!orderMatchesCaseStyleFilter(order, caseStyleFilter)) return false;
    if (designFilter === "custom" && !isOrderCustom(order)) return false;
    if (designFilter === "normal" && !isOrderNonCustom(order)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  };

  const copyOrdersToTeePlusSheet = async (orders, successLabel) => {
    if (orders.length === 0) {
      toast.warn("لا توجد طلبات للنسخ");
      return;
    }

    const lines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of orders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );
        if (count > 0) {
          hasDuplicate = true;
          lines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }
        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
        }`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${order.state} (D)\t${order.total}\tCOD\t`;
        order.items.forEach((item, index) => {
          const itemLine = `${index + 1}-${order.items.length}${teePlusPackedItemData(item)}`;
          if (index === 0) lines.push(buildFirstItemLine(customerBase, itemLine, order.note));
          else lines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        });
        const match = currentRef.match(/(\d+)$/);
        if (match) currentRef = currentRef.replace(/\d+$/, (parseInt(match[0], 10) + 1).toString());
      } catch (err) {
        lines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);

    const copied = await copyTextToClipboard(lines.join("\n"));
    if (!copied) {
      toast.error("فشل في النسخ، حاول مرة تانية");
      return;
    }
    toast.success(
      `${successLabel} (${orders.length})${hasDuplicate ? " — يوجد تكرار في بعض الأرقام" : ""}`,
      { autoClose: 6000 }
    );
  };

  const handleCopyAllVisible = async () => {
    const orders = loadedOrders.filter((order) => matchesListFilters(order) && isOrderNonCustom(order));
    if (orders.length === 0) {
      toast.warn("لا توجد طلبات ظاهرة للنسخ");
      return;
    }
    if (
      !window.confirm(
        `نسخ كل الظاهر (${orders.length} طلب) إلى الإكسيل؟\nسيتم فحص التكرار وزيادة رقم المرجع تلقائيًا.`
      )
    ) {
      return;
    }
    await copyOrdersToTeePlusSheet(orders, "تم نسخ الطلبات الظاهرة");
  };

  const handleSendSelectedToTeePlus = async () => {
    const chosen = Array.from(selectedOrders.values());
    if (chosen.length === 0) {
      toast.warn("حدد الطلبات المطلوب إرسالها");
      return;
    }
    const customCount = chosen.filter(isOrderCustom).length;
    const orders = chosen.filter(isOrderNonCustom);
    if (orders.length === 0) {
      toast.warn("الطلبات المحددة مخصصة ولن تُنسخ إلى TeePlus");
      return;
    }
    if (
      !window.confirm(
        `إرسال ${orders.length} طلب محدد إلى TeePlus؟\nهيتم النسخ بنفس صيغة TeePlus الحالية، بدون إرسال باقي الطلبات.` +
          (customCount > 0 ? `\nسيتم تخطي ${customCount} طلب مخصص.` : "")
      )
    ) {
      return;
    }
    await copyOrdersToTeePlusSheet(orders, "تم إرسال الطلبات المحددة إلى TeePlus");
  };

  const handleCopyOrder = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );
      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${order.state} (D)\t${order.total}\tCOD\t`;
      const lines = [];
      order.items.forEach((item, index) => {
        const itemLine = `${index + 1}-${order.items.length}${teePlusPackedItemData(item)}`;
        if (index === 0) lines.push(buildFirstItemLine(customerBase, itemLine, order.note));
        else lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
      });

      const copied = await copyTextToClipboard(lines.join("\n"));
      if (!copied) throw new Error("COPY_FAILED");
      toast.success("تم نسخ الطلب بنجاح!");
    } catch (err) {
      console.error(err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      if (!window.confirm("هل تريد تحديث الطلب إلى قديم؟")) return;
      try {
        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
        forgetOrders([orderId]);
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      openOrderChanges(orderId);
      return;
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

    if (!window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`)) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
      forgetOrders([orderId]);
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`)) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      forgetOrders([orderId]);
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleConvertAllVisibleToOld = async () => {
    if (!canAct) return;
    const visible = loadedOrders.filter(matchesListFilters);
    if (visible.length === 0) {
      toast.info("لا توجد طلبات ظاهرة");
      return;
    }
    if (
      !window.confirm(
        `تحويل كل الظاهر (${visible.length} طلب) إلى قديم؟\nالطلبات هتختفي من الصفحة فورًا.`
      )
    ) {
      return;
    }

    setIsBulkUpdating(true);
    try {
      const result = await updateOrderStatusBulk(
        visible.map((o) => o.orderNumber),
        "Old",
        "تم التحديث الجماعي من طابور Tee Plus"
      );
      const okIds = new Set((result.results || []).filter((r) => r.ok).map((r) => r.orderId));
      const failed = (result.results || []).filter((r) => !r.ok);
      setLoadedOrders((prev) => prev.filter((o) => !okIds.has(o.orderNumber)));
      forgetOrders(okIds);
      toast.success(`تم تحويل ${okIds.size} طلب(ات) إلى قديم`);
      failed.slice(0, 5).forEach((r) =>
        toast.error(`#${r.orderId}: ${typeof r.error === "string" ? r.error : "فشل التحديث"}`)
      );
    } catch (err) {
      toast.error(err.message || "فشل التحويل إلى قديم");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const filteredOrders = loadedOrders.filter(matchesListFilters);

  const busy = isOrdersLoading || isBulkUpdating || isRefreshingStock;
  const stockCounts = stockInfo?.orders || {};
  const unclassifiedCount = stockCounts.unclassified || 0;
  const visibleTabs = unclassifiedCount > 0
    ? [
        ...STOCK_TABS,
        { id: "Unclassified", label: "بدون تصنيف", english: "Unclassified", countKey: "unclassified", active: "bg-orange-600 text-white", idle: "bg-white text-orange-800 border border-orange-200" },
      ]
    : STOCK_TABS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 md:p-8" dir="rtl">
      {changesDialog}
      <ToastContainer position="bottom-left" autoClose={2500} hideProgressBar newestOnTop closeOnClick pauseOnHover={false} rtl limit={3} />

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Shirt className="text-emerald-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              طلبات Tee Plus
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {isLoadingStock && !stockInfo && (
          <div className="bg-emerald-50 text-emerald-800 p-4 rounded-xl mb-6 border border-emerald-200">
            جاري فحص مخزون TeePlus...
          </div>
        )}

        {stockInfo && (!stockInfo.success || stockInfo.stale) && (
          <div className="bg-red-100 text-red-800 p-4 rounded-xl mb-6 border border-red-200">
            <p className="font-bold">Stock Check Failed</p>
            <p className="text-sm mt-1">
              تعذر الاتصال بسيرفر مخزون TeePlus. التصنيف الحالي للطلبات لم يتغير.
              {stockInfo.cached && !stockInfo.stale ? " البيانات المعروضة من آخر فحص ناجح وما زالت ضمن مدة التخزين." : ""}
              {stockInfo.stale ? " آخر بيانات محفوظة قديمة ولن تُستخدم لإعادة التصنيف." : ""}
            </p>
          </div>
        )}

        {error && error !== "Stock Check Failed" && (
          <div className="bg-red-100 text-red-700 p-4 rounded-xl mb-6 border border-red-200">{error}</div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Last Stock Check</p>
              <p className="text-xl font-bold text-emerald-800">{formatStockCheckTime(stockInfo?.checkedAt)}</p>
              {stockInfo?.cached && (
                <p className="text-xs text-amber-700 mt-1">
                  {stockInfo.stale ? "Cached / stale" : "Cached"}
                  {stockInfo.expiresAt ? ` — حتى ${formatStockCheckTime(stockInfo.expiresAt)}` : ""}
                </p>
              )}
            </div>
            {adminUser && (
              <button
                type="button"
                onClick={handleRefreshStock}
                disabled={busy}
                className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <RefreshCw size={18} className={isRefreshingStock ? "animate-spin" : ""} />
                تحديث المخزون
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStockTab(tab.id)}
                className={`rounded-xl px-4 py-3 text-right transition-all ${stockTab === tab.id ? tab.active : tab.idle}`}
              >
                <div className="text-sm opacity-80">{tab.english}</div>
                <div className="text-lg font-bold">{tab.label} ({stockCounts[tab.countKey] || 0})</div>
              </button>
            ))}
          </div>
        </div>

        {adminUser && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
            <div className="flex items-center gap-3 mb-4">
              <Package className="text-emerald-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">عدد الطلبات</h2>
            </div>
            {isLoadingSummary ? (
              <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
            ) : (
              <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-xl">
                <span className="text-gray-600">إجمالي طلبات Tee Plus</span>
                <span className="text-2xl font-bold text-emerald-600">{teePlusSummary?.count ?? 0}</span>
              </div>
            )}
          </div>
        )}

        {adminUser && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
            <div className="flex items-center gap-3 mb-4">
              <Hash className="text-emerald-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                  ابدأ من (مثال: Girabatcom.150)
                </label>
                <input
                  type="text"
                  value={referenceInput}
                  onChange={(e) => setReferenceInput(e.target.value)}
                  onBlur={() => {
                    if (referenceInput.trim() && referenceInput.includes(".")) {
                      setCurrentReference(referenceInput.trim());
                      localStorage.setItem("orderReference", referenceInput.trim());
                      toast.success("تم تحديث رقم المرجع!");
                    }
                  }}
                  className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-emerald-300 focus:border-emerald-400 transition-all"
                  placeholder="Girabatcom.4000"
                />
              </div>
              <button
                onClick={() => {
                  if (referenceInput.trim()) {
                    setCurrentReference(referenceInput.trim());
                    setReferenceInput(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl hover:shadow-xl font-bold flex items-center gap-2"
              >
                <Save size={20} />
                تحديث الرقم
              </button>
              <button
                onClick={handleSendSelectedToTeePlus}
                disabled={busy || selectedOrders.size === 0}
                className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send size={20} />
                إرسال المحدد إلى TeePlus ({selectedOrders.size})
              </button>
              <button
                onClick={handleCopyAllVisible}
                disabled={busy || filteredOrders.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={20} />
                نسخ كل الظاهر ({filteredOrders.length})
              </button>
              <button
                onClick={handleConvertAllVisibleToOld}
                disabled={busy || filteredOrders.length === 0}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Archive size={20} />
                تحويل كل الظاهر لقديم ({filteredOrders.length})
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-3 text-right">
              إرسال المحدد ينسخ الطلبات التي تختارها فقط إلى TeePlus. لا يتم إرسال كل المتوفر أو المحدود تلقائيًا.
            </p>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 relative w-full">
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-emerald-300"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl bg-white"
                disabled={isOrdersLoading}
              >
                {[10, 20, 30, 50, 80, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} طلب
                  </option>
                ))}
              </select>
              <span className="text-gray-600">الظاهر: {filteredOrders.length}</span>
            </div>
          </div>
          <CaseStyleOrderFilter
            value={caseStyleFilter}
            onChange={setCaseStyleFilter}
            orders={loadedOrders}
            accent="purple"
          />
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-600">نوع التصميم:</span>
            {[
              { value: "all", label: "كل الطلبات" },
              { value: "normal", label: "طلبات عادية" },
              { value: "custom", label: "طلبات مخصصة" },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setDesignFilter(value)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  designFilter === value
                    ? value === "custom"
                      ? "bg-green-600 text-white shadow-md"
                      : value === "normal"
                        ? "bg-gray-600 text-white shadow-md"
                        : "bg-emerald-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <div className="flex items-center justify-center gap-2 text-emerald-600 py-16">
              <Loader className="animate-spin" size={24} />
              جاري التحميل...
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-16">
              <Package className="mx-auto mb-4 text-gray-400" size={64} />
              <p className="text-2xl text-gray-700">لا توجد طلبات في هذا التصنيف</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-[1000px] table-auto">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b-2 border-emerald-200">
                    {adminUser && (
                      <th className="p-4 text-center font-bold text-emerald-800">
                        <div className="flex flex-col items-center gap-1">
                          <input
                            type="checkbox"
                            className="w-5 h-5 accent-emerald-600"
                            checked={filteredOrders.length > 0 && filteredOrders.every((order) => selectedOrders.has(order.orderNumber))}
                            onChange={(e) => setSelectAllVisible(e.target.checked, filteredOrders)}
                          />
                          <span className="text-xs">تحديد</span>
                        </div>
                      </th>
                    )}
                    <th className="p-4 text-center font-bold text-emerald-800">الطلب</th>
                    <th className="p-4 text-right font-bold text-emerald-800">العميل</th>
                    <th className="p-4 text-right font-bold text-emerald-800">العنوان</th>
                    <th className="p-4 text-center font-bold text-emerald-800">الإجمالي</th>
                    <th className="p-4 text-right font-bold text-emerald-800">المنتجات</th>
                    <th className="p-4 text-right font-bold text-emerald-800">ملاحظات</th>
                    <th className="p-4 text-center font-bold text-emerald-800">الإنتاج</th>
                    <th className="p-4 text-center font-bold text-emerald-800">إجراءات</th>
                    <th className="p-4 text-center font-bold text-emerald-800">حذف</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60">
                      {adminUser && (
                        <td className="p-4 text-center">
                          <input
                            type="checkbox"
                            className="w-5 h-5 accent-emerald-600"
                            checked={selectedOrders.has(order.orderNumber)}
                            onChange={() => toggleSelectOrder(order)}
                          />
                        </td>
                      )}
                      <td className="p-4 text-center font-mono font-bold text-emerald-700">#{order.orderNumber}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-gray-500" />
                          <span style={{ fontFamily: "cairo black" }}>{order.name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-green-700 mt-1">
                          <Phone size={14} />
                          {order.phoneNumber}
                        </div>
                        {order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" && (
                          <div className="flex items-center gap-1 text-green-700 text-sm">
                            <Phone size={12} />
                            {order.phoneNumber2}
                          </div>
                        )}
                      </td>
                      <td className="p-4 max-w-[220px]">
                        <div className="flex items-start gap-2">
                          <MapPin size={16} className="text-gray-500 mt-1 shrink-0" />
                          <span className="text-sm">{order.address}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <MapIcon size={16} className="text-gray-500" />
                          <span className="text-purple-600">{order.state}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center font-bold text-green-600">{order.total} ج.م</td>
                      <td className="p-4">
                        {order.items.map((item, index) => (
                          <div key={`${item.id}-${index}`} className="mb-2">
                            <div className="font-medium text-emerald-800">{item.name}</div>
                            <div className="text-sm">{item.type}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-1 rounded font-mono ${item.code?.startsWith("CUSTOM") ? "bg-green-100" : "bg-gray-100"}`}>
                                {item.code}
                              </span>
                              {item.driveLink && (
                                <div className="flex items-center gap-1">
                                  <a href={item.driveLink} target="_blank" rel="noopener noreferrer" className="text-emerald-600" title="فتح الرابط">
                                    <LinkIcon size={16} />
                                  </a>
                                  {item.code?.startsWith("CUSTOM") && (
                                    <button
                                      type="button"
                                      onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                      className="flex items-center gap-2 text-white px-3 py-2"
                                      title="تحميل الصورة"
                                      style={{ backgroundColor: "#15d225", borderRadius: "4px" }}
                                    >
                                      <Download size={16} />
                                      <span className="text-sm">Download</span>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </td>
                      <td className="p-4 text-gray-600">{order.note || "—"}</td>
                      <td className="p-4">
                        {adminUser ? (
                          <div className="flex flex-col gap-2 min-w-[100px]">
                            <button
                              onClick={() => handleCopyOrder(order)}
                              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                            >
                              <Copy size={16} />
                              نسخ
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Printlet")}
                              className="bg-fuchsia-600 text-white px-3 py-2 rounded-lg hover:bg-fuchsia-700 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Send size={14} />
                                Printlet
                              </div>
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Printing Hold")}
                              className="bg-slate-700 text-white px-3 py-2 rounded-lg hover:bg-slate-800 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Printer size={14} />
                                PrintHold
                              </div>
                            </button>
                          </div>
                        ) : (
                          <p className="text-center text-sm text-slate-500">عرض فقط</p>
                        )}
                      </td>
                      <td className="p-4">
                        {canAct ? (
                          <div className="flex flex-col gap-2 min-w-[100px]">
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                              className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Clock size={14} />
                                معلق
                              </div>
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                              className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Edit size={14} />
                                تغييرات
                              </div>
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                              className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <DollarSign size={14} />
                                ديبوزت
                              </div>
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                              className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Archive size={14} />
                                قديم
                              </div>
                            </button>
                          </div>
                        ) : (
                          <p className="text-center text-sm text-slate-500">عرض فقط</p>
                        )}
                      </td>
                      <td className="p-4">
                        {canAct ? (
                          <div className="flex flex-col gap-2 min-w-[80px]">
                            <button
                              onClick={() => handleDeleteOrder(order.orderNumber)}
                              className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Trash2 size={14} />
                                حذف
                              </div>
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                              className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <AlertTriangle size={14} />
                                Spam
                              </div>
                            </button>
                          </div>
                        ) : (
                          <p className="text-center text-sm text-slate-500">عرض فقط</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMoreOrders}
                    disabled={isLoadingMore}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-bold disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        جاري التحميل...
                      </>
                    ) : (
                      <>
                        <ChevronLeft size={20} />
                        تحميل المزيد
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeePlusOrdersPage;
